// System prompts for each generation stage

import { CompetitorData, KeywordData, ProjectContext, CompetitorMetrics } from './types';

// Розраховуємо середні метрики по конкурентам
function calculateCompetitorMetrics(competitors: CompetitorData[]): CompetitorMetrics {
  console.log('📊 Calculating metrics from competitors:', competitors.map(c => ({
    domain: c.domain,
    word_count: c.word_count,
    h2_count: c.headers.h2?.length || 0,
    h3_count: c.headers.h3?.length || 0
  })));

  const validCompetitors = competitors.filter(c => 
    (c.word_count && c.word_count > 0) || (c.text && c.text.length > 100)
  );

  console.log(`✅ Valid competitors: ${validCompetitors.length} of ${competitors.length}`);

  if (validCompetitors.length === 0) {
    return {
      avgWordCount: 2500,
      avgH2Count: 8,
      avgH3Count: 20,
      maxWordCount: 3500,
      minWordCount: 1500
    };
  }

  const wordCounts = validCompetitors.map(c => 
    c.word_count || (c.text ? c.text.split(/\s+/).filter(Boolean).length : 0)
  ).filter(count => count > 0);
  
  const h2Counts = validCompetitors.map(c => c.headers.h2?.length || 0).filter(count => count > 0);
  const h3Counts = validCompetitors.map(c => c.headers.h3?.length || 0).filter(count => count > 0);

  const avgWordCount = wordCounts.length > 0 
    ? Math.round(wordCounts.reduce((a, b) => a + b, 0) / wordCounts.length) 
    : 2500;
  const avgH2Count = h2Counts.length > 0 
    ? Math.round(h2Counts.reduce((a, b) => a + b, 0) / h2Counts.length) 
    : 8;
  const avgH3Count = h3Counts.length > 0 
    ? Math.round(h3Counts.reduce((a, b) => a + b, 0) / h3Counts.length) 
    : 20;

  console.log('📊 Competitor Metrics:', {
    avgWordCount,
    avgH2Count,
    avgH3Count,
    validCompetitors: validCompetitors.length,
    wordCounts,
    h2Counts,
    h3Counts
  });

  return {
    avgWordCount,
    avgH2Count,
    avgH3Count,
    maxWordCount: Math.max(...wordCounts, 1500),
    minWordCount: Math.min(...wordCounts, 500)
  };
}

// Визначаємо мову контенту
function detectLanguage(keywords: KeywordData, competitors: CompetitorData[]): string {
  // Беремо зразок тексту з ключових слів та заголовків
  const sampleTexts = [
    ...keywords.additional.slice(0, 3),
    ...keywords.missing.slice(0, 3),
    ...competitors.slice(0, 2).flatMap(c => c.headers.h2?.slice(0, 2) || [])
  ].join(' ').toLowerCase();

  // Визначаємо мову за характерними символами/словами
  if (/[а-яё]/i.test(sampleTexts) && /\b(и|в|на|с|для|как|что|это)\b/.test(sampleTexts)) {
    return 'ru'; // Російська
  }
  if (/[а-яіїєґ]/i.test(sampleTexts) && /\b(і|на|для|як|що|це|та)\b/.test(sampleTexts)) {
    return 'uk'; // Українська
  }
  if (/[áàâãéêíóôõúç]/i.test(sampleTexts) || /\b(o|a|de|para|com|que|como)\b/.test(sampleTexts)) {
    return 'pt'; // Португальська
  }
  if (/[áéíóúñ]/i.test(sampleTexts) || /\b(el|la|de|para|con|que|como|los|las)\b/.test(sampleTexts)) {
    return 'es'; // Іспанська
  }
  
  return 'en'; // За замовчуванням англійська
}

export function buildOutlinePrompt(
  competitors: CompetitorData[],
  keywords: KeywordData,
  projectContext: ProjectContext,
  language?: string,
  contentType?: string,
  intent?: string,
  extractedData?: string
): string {
  // Розраховуємо метрики конкурентів
  const metrics = calculateCompetitorMetrics(competitors);
  
  // Визначаємо мову якщо не вказана
  const detectedLanguage = language || detectLanguage(keywords, competitors);
  
  // Мовні інструкції НА ТІЙ ЖЕ МОВІ
  const languageInstructions: Record<string, string> = {
    uk: `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🌍 КРИТИЧНА ВИМОГА ДО МОВИ:
Ти МАЄШ писати ВИКЛЮЧНО українською мовою.
Кожне слово, речення та параграф мають бути українською.
НЕ змішуй мови ні за яких обставин!
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`,
    ru: `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🌍 КРИТИЧЕСКОЕ ТРЕБОВАНИЕ К ЯЗЫКУ:
Ты ДОЛЖЕН писать ИСКЛЮЧИТЕЛЬНО на русском языке.
Каждое слово, предложение и абзац должны быть на русском.
НЕ смешивай языки ни при каких обстоятельствах!
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`,
    en: `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🌍 CRITICAL LANGUAGE REQUIREMENT:
You MUST write EXCLUSIVELY in English.
Every single word, sentence, and paragraph must be in English.
Do NOT mix languages under any circumstances!
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`,
    pl: `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🌍 KRYTYCZNY WYMÓG JĘZYKOWY:
MUSISZ pisać WYŁĄCZNIE po polsku.
Każde słowo, zdanie i akapit muszą być po polsku.
NIE mieszaj języków w żadnych okolicznościach!
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`
  };
  
  const languageInstruction = languageInstructions[detectedLanguage] || languageInstructions.en;

  // Content characteristics
  const contentInfo = contentType || intent ? `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📊 CONTENT CHARACTERISTICS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${contentType ? `Content Type: ${contentType}` : ''}
${intent ? `Search Intent: ${intent}` : ''}

Adapt your writing style and structure according to these characteristics.
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
` : '';

  // Real data from target page
  const urlDataSection = extractedData ? `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔍 REAL DATA FROM TARGET PAGE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${extractedData}

🚨 CONTENT RULES - MANDATORY COMPLIANCE

⛔ STRICTLY FORBIDDEN:
• Using Russian rubles (руб., рублей, RUB, ₽) in ANY form
• Writing SPECIFIC PRICES in article text (e.g. "5000 грн", "$299")
  → Prices are DYNAMIC and will become outdated
• Converting prices from one currency to another
• Creating price ranges with specific numbers from extracted data
• Inventing phone numbers, contacts, addresses, or brands
• Making up product specifications not shown in real data

✅ REQUIRED APPROACH:
• Use GENERAL pricing language:
  "affordable prices", "competitive pricing", "wide price range"
• Use RELATIVE price segments:
  "budget-friendly options", "premium segment", "mid-range pricing"
• Focus on VALUE & QUALITY over specific numbers
• Non-price numbers ARE ALLOWED:
  years in business, team size, projects completed, success rates

⭐ PRIORITIZE:
• Unique competitive advantages from real data
• Company credentials and certifications
• Specific expertise and differentiators
• Customer value proposition

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
` : '';
  
  // Форматуємо заголовки конкурентів у простий список
  const competitorHeadings = competitors
    .slice(0, 10)
    .flatMap(comp => {
      const headers: string[] = [];
      if (comp.headers.h1?.length) headers.push(...comp.headers.h1);
      if (comp.headers.h2?.length) headers.push(...comp.headers.h2);
      if (comp.headers.h3?.length) headers.push(...comp.headers.h3.slice(0, 10));
      return headers;
    })
    .filter(Boolean)
    .join('\n');

  // Всі ключові слова разом
  const allKeywords = [
    ...keywords.additional,
    ...keywords.missing.slice(0, 20)
  ].join('\n');

  const instruction = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📋 TASK: CREATE ARTICLE STRUCTURE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

COMPETITOR BENCHMARK (Top ${competitors.length}):
• Average word count: ${metrics.avgWordCount} words
  (range: ${metrics.minWordCount}–${metrics.maxWordCount})
• Average H2 count: ${metrics.avgH2Count}
• Average H3 count: ${metrics.avgH3Count}

YOUR TARGET (Beat Competitors):
• Total words: ${Math.round(metrics.avgWordCount * 1.15)}–${Math.round(metrics.avgWordCount * 1.25)} words (15–25% more content)
• H2 sections: ${metrics.avgH2Count}–${metrics.avgH2Count + 2} (match or exceed)
• H3 subheadings: ${metrics.avgH3Count}–${metrics.avgH3Count + 5} (more detail)
• FAQ section: 5–7 questions (MANDATORY)

🎯 STRUCTURE REQUIREMENTS:

1. Create proper heading hierarchy (not random headings)
2. Each section covers DIFFERENT aspect/topic
3. NO duplicate information between sections
4. Each H3 must have UNIQUE content focus

Example of BAD structure (avoid):
  Section 2: "Benefits for home" → Section 5: "Home advantages" ❌ (repetition)

Example of GOOD structure:
  Technical specs → Benefits → Usage → Maintenance → Selection guide ✅

🚨 JSON STRUCTURE RULES:

• "sections" array = Main content sections ONLY
• "faq" object = Separate FAQ section
• FAQ is added AUTOMATICALLY at the END
• DO NOT include FAQ in sections[] array`;

  const critical = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
OUTPUT REQUIREMENTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
CRITICAL: Return ONLY valid JSON.
• No explanations before or after
• No markdown code blocks
• No additional text
• Pure JSON object only
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;

  return `${languageInstruction}

${contentInfo}

${urlDataSection}

${instruction}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
REFERENCE MATERIALS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

COMPETITOR HEADERS (inspiration only, not verbatim):
${competitorHeadings}

TARGET KEYWORDS:
Primary: ${keywords.additional.slice(0, 3).join(', ')}
Secondary: ${keywords.missing.slice(0, 10).join(', ')}

KEYWORD INTEGRATION GUIDELINES:
• Use exact match ONLY when natural
• Rephrase if awkward
• Blend organically into headings and text
• Quality and readability > keyword density
• NO keyword stuffing

COMPANY CONTEXT:
${projectContext.description}

${projectContext.project_prompt ? `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PROJECT-SPECIFIC INSTRUCTIONS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${projectContext.project_prompt}

These instructions define:
• Tone of voice and writing style
• Target audience positioning
• Brand requirements and values
• Content priorities and focus areas

Follow these carefully in all content decisions.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
` : ''}

${critical}

JSON FORMAT SPECIFICATION:
{
  "h1": "H1 title with keywords",
  "metaDescription": "Description 150-160 characters",
  "introduction": {
    "h2": "",
    "h3": [],
    "targetKeywords": {
      "primary": "main keyword",
      "secondary": ["keyword1", "keyword2"]
    },
    "estimatedWordCount": 250
  },
  "sections": [
    {
      "h2": "Main Topic Section",
      "h3": ["Subtopic 1", "Subtopic 2", "Subtopic 3"],
      "targetKeywords": {
        "primary": "main keyword",
        "secondary": ["additional1", "additional2"]
      },
      "estimatedWordCount": ${Math.round(metrics.avgWordCount / Math.max(metrics.avgH2Count, 1))}
    }
  ],
  "conclusion": {
    "h2": "",
    "h3": [],
    "targetKeywords": {
      "primary": "main keyword",
      "secondary": []
    },
    "estimatedWordCount": 250
  },
  "cta": {
    "h2": "",
    "h3": [],
    "targetKeywords": {
      "primary": "",
      "secondary": []
    },
    "estimatedWordCount": 150
  },
  "faq": {
    "h2": "FAQ",
    "h3": ["Question 1?", "Question 2?", "Question 3?", "Question 4?", "Question 5?"],
    "targetKeywords": {
      "primary": "main keyword",
      "secondary": []
    },
    "estimatedWordCount": 400
  }
}`;
}

export function buildSectionPrompt(
  sectionH2: string,
  h3s: string[],
  targetKeywords: { primary: string; secondary: string[] },
  projectContext: ProjectContext,
  competitorExamples: string[],
  previousSections: { h2: string; html: string }[], // ВЖЕ написані секції
  totalSections: number,
  currentSectionIndex: number,
  targetWordCount: number, // Цільова к-сть слів для цієї секції
  humanizeContent = false, // Опція гуманізації контенту
  language?: string,
  contentType?: string,
  intent?: string,
  extractedData?: any
): string {
  console.log('🌐 [buildSectionPrompt] Language parameter:', language);
  
  // Перевіряємо чи це FAQ секція
  const isFAQ = sectionH2.toLowerCase().includes('faq') || 
                sectionH2.toLowerCase().includes('питання') ||
                sectionH2.toLowerCase().includes('вопрос') ||
                sectionH2.toLowerCase().includes('questions');
  
  // Language instruction
  const languageNames: Record<string, { native: string; english: string }> = {
    uk: { native: 'українська мова', english: 'Ukrainian' },
    ru: { native: 'русский язык', english: 'Russian' },
    en: { native: 'English', english: 'English' },
    pl: { native: 'polski', english: 'Polish' },
    de: { native: 'Deutsch', english: 'German' },
    fr: { native: 'français', english: 'French' },
    es: { native: 'español', english: 'Spanish' },
    it: { native: 'italiano', english: 'Italian' },
    pt: { native: 'português', english: 'Portuguese' }
  };

  const langInfo = languageNames[language || 'uk'] || languageNames.uk;
  console.log('🌐 [buildSectionPrompt] Using language:', langInfo.english);
  
  // Language instruction in the SAME language as content
  const languageInstructions: Record<string, string> = {
    uk: `
═══════════════════════════════════════════════════════════════
🌐 КРИТИЧНА ВИМОГА ДО МОВИ 🌐
═══════════════════════════════════════════════════════════════

Ти МАЄШ писати ВИКЛЮЧНО українською мовою.

ЗАБОРОНЕНО:
❌ Змішувати мови (наприклад, українська + російська)
❌ Використовувати слова з інших мов
❌ Транслітерація або перемикання мов

ОБОВ'ЯЗКОВО:
✅ 100% чиста українська мова
✅ Рідна лексика та граматика
✅ Природні українські вирази

═══════════════════════════════════════════════════════════════
`,
    ru: `
═══════════════════════════════════════════════════════════════
🌐 КРИТИЧЕСКОЕ ТРЕБОВАНИЕ К ЯЗЫКУ 🌐
═══════════════════════════════════════════════════════════════

Ты ДОЛЖЕН писать ИСКЛЮЧИТЕЛЬНО на русском языке.

ЗАПРЕЩЕНО:
❌ Смешивать языки (например, русский + украинский)
❌ Использовать слова из других языков
❌ Транслитерация или переключение языков

ОБЯЗАТЕЛЬНО:
✅ 100% чистый русский язык
✅ Родная лексика и грамматика
✅ Естественные русские выражения

═══════════════════════════════════════════════════════════════
`,
    en: `
═══════════════════════════════════════════════════════════════
🌐 CRITICAL LANGUAGE REQUIREMENT 🌐
═══════════════════════════════════════════════════════════════

You MUST write EXCLUSIVELY in English.

FORBIDDEN:
❌ Mixing languages
❌ Using words from other languages
❌ Transliterations or code-switching

REQUIRED:
✅ 100% pure English
✅ Native vocabulary and grammar
✅ Natural English expressions

═══════════════════════════════════════════════════════════════
`,
    pl: `
═══════════════════════════════════════════════════════════════
🌐 KRYTYCZNY WYMÓG JĘZYKOWY 🌐
═══════════════════════════════════════════════════════════════

MUSISZ pisać WYŁĄCZNIE po polsku.

ZABRONIONE:
❌ Mieszanie języków
❌ Używanie słów z innych języków
❌ Transliteracja lub przełączanie języków

WYMAGANE:
✅ 100% czysty język polski
✅ Rodzima leksyka i gramatyka
✅ Naturalne polskie wyrażenia

═══════════════════════════════════════════════════════════════
`
  };
  
  const languageInstruction = languageInstructions[language || 'uk'] || languageInstructions.en;

  // Критична вимога про тире
  const dashRequirement = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PUNCTUATION RULES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

ALLOWED DASHES:
• Hyphen (-) for compound words: "SEO-optimization"
• En dash (–) for ranges: "2024–2026", "Monday–Friday"

FORBIDDEN:
• Em dash (—) NEVER USE
• Long dash (―) NEVER USE

EXAMPLES:
❌ "Service — best choice" (em dash)
❌ "Price 1000—2000" (em dash)
✅ "Service – best choice" (en dash)
✅ "Price 1000–2000" (en dash)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;

  // Секція з реальними даними (якщо є)
  const realDataSection = extractedData ? `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
REAL DATA FROM TARGET PAGE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${typeof extractedData === 'string' ? extractedData : JSON.stringify(extractedData, null, 2)}

CRITICAL CONTENT RULES:

PRICING POLICY:
• NEVER mention Russian rubles (руб., рублей, RUB, ₽)
• NEVER write specific prices ("5000 грн", "$299")
• USE general terms: "affordable pricing", "competitive rates"
• FOCUS on value proposition, not price numbers
• If page has ruble prices - omit price mention completely

DATA ACCURACY:
• Use ONLY factual information from extracted data
• NO invented numbers, statistics, or claims
• NO conversions or estimations
• Include specific credentials, years in business, certificates
• Highlight unique differentiators and competitive advantages

UNIQUENESS REQUIREMENTS:
• Include company-specific approach and methodology
• Mention concrete achievements and success rates
• Use specific facts, not generic statements
• Reference actual qualifications and certifications

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
` : '';

  const previousContent = previousSections.length > 0
    ? `\n\nALREADY WRITTEN (keep in context, DO NOT repeat):\n${previousSections.map(s => `<h2>${s.h2}</h2>\n${s.html.substring(0, 500)}...`).join('\n\n')}`
    : '';

  const competitorContext = competitorExamples.length > 0
    ? `\n\nCompetitor examples:\n${competitorExamples.slice(0, 2).map((ex, i) => `${i + 1}. ${ex.substring(0, 300)}...`).join('\n\n')}`
    : '';

  const wordsPerH3 = Math.round(targetWordCount / Math.max(h3s.length, 1));

  const isIntro = sectionH2 === '';
  
  // Спеціальні інструкції для FAQ
  const faqInstructions = isFAQ ? `

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
FAQ SECTION FORMAT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

STRUCTURE FOR EACH QUESTION:
<h3>Question text?</h3>
<p>Short answer in 1-3 sentences. Direct and clear.</p>

FORBIDDEN:
• Long paragraphs (>3 sentences)
• Bullet or numbered lists
• Tables
• Multiple paragraphs per question
• Complex formatting

REQUIRED STYLE:
• Brief answers (50-100 words max per question)
• Direct answer first
• 1-2 supporting details if needed
• Simple <p> tags only
• <strong> only for critical terms

TARGET LENGTH:
${h3s.length} questions × 60-80 words = ~${h3s.length * 70} words total

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
` : '';

    const qualityCheck = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
QUALITY STANDARDS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

LANGUAGE QUALITY:
• Verify spelling and grammar
• Use correct word forms and cases
• Double-check technical terminology

CONTENT UNIQUENESS:
• NO repetition from previous sections
• Each paragraph provides NEW information
• Different perspectives, not duplicates
• Complement previous content, don't repeat it

COHERENCE:
• Logical flow within section
• Smooth transitions between topics
• Consistent terminology

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
    
    const sectionTitle = isIntro ? 'INTRODUCTION (NO H2 heading)' : `<h2>${sectionH2}</h2>`;
    
    console.log('🌐 [buildSectionPrompt] Language instruction length:', languageInstruction.length);
    console.log('🌐 [buildSectionPrompt] First 200 chars of prompt:', `${languageInstruction}\n\nYou're writing an article sequentially.`.substring(0, 200));
    
    return `${languageInstruction}

${dashRequirement}

${qualityCheck}

${realDataSection}

You're writing an article sequentially. Now section ${currentSectionIndex + 1} of ${totalSections}.

${previousContent}

NOW WRITE: ${sectionTitle}

${faqInstructions}

${h3s.length > 0 ? `STRUCTURE (MUST use these H3s):
${h3s.map(h3 => `<h3>${h3}</h3>`).join('\n')}` : ''}

TARGET VOLUME: ~${isFAQ ? h3s.length * 70 : targetWordCount} words${h3s.length > 0 && !isFAQ ? ` (~${wordsPerH3} words per H3)` : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
KEYWORDS TO INTEGRATE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Primary: ${targetKeywords.primary}
Secondary: ${targetKeywords.secondary.join(', ')}

KEYWORD USAGE GUIDELINES:
• Use exact match ONLY when naturally fits
• Rephrase with synonyms if awkward
• Blend organically into text
• NO bold highlighting of keywords
• NO forced keyword insertion
• Quality and readability > keyword density

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${competitorContext}

${projectContext.project_prompt ? `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PROJECT-SPECIFIC INSTRUCTIONS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${projectContext.project_prompt}

Apply these instructions carefully:
• Tone of voice and style
• Target audience positioning
• Brand requirements
• Content priorities

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
` : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SECTION FOCUS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${!isIntro ? `
Write ONLY about: "${sectionH2}"

• Stay strictly on topic
• Each H3 addresses its specific subtopic
• NO general information unrelated to section theme
• NO topic drift or tangents

Example:
Topic: "Tinder Alternatives"
❌ WRONG: "Tinder is popular dating app..."
✅ RIGHT: "Bumble - alternative where women initiate..."
` : ''}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${humanizeContent ? `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
NATURAL WRITING STYLE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

SENTENCE VARIATION:
• Vary lengths dramatically (3-25+ words)
• Mix simple and complex structures
• Occasional incomplete thoughts acceptable
• Avoid uniform patterns

LANGUAGE NATURALNESS:
• NO overused transitions: "however", "notably", "particularly"
• Use single adjectives, not pairs
• Include genuine emotion
• Informal language when appropriate

PARAGRAPH STRUCTURE:
• Vary lengths (1-8+ sentences)
• NO recaps every 2-3 paragraphs
• Abrupt tonal shifts allowed
• Mix dense and choppy sentences

MAINTAIN word count - don't reduce volume!

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
` : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SECTION REQUIREMENTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${extractedData ? `DATA ACCURACY (CRITICAL):
• NO Russian rubles (руб., рублей, RUB, ₽)
• NO specific prices ("5000 грн", "$299")  
• NO price conversions or inventions
• USE general value terms: "affordable", "competitive pricing"
• FOCUS on quality and benefits, not price numbers
• INCLUDE unique facts: credentials, years, success rates
• MENTION competitive advantages and differentiators

` : ''}1. ${isIntro ? 'Opening paragraph' : isFAQ ? 'Short FAQ answers' : 'Section intro'} (${isFAQ ? '60-80' : '80-120'} words):
   • ${isIntro ? 'Hook the reader' : isFAQ ? 'Brief direct answers only' : 'Continue logically from previous sections'}
   ${!isFAQ ? `• First sentence includes "${targetKeywords.primary}"` : '• Keep answers concise'}

2. ${h3s.length > 0 ? (isFAQ ? `FOR EACH QUESTION (60-80 words max)` : `FOR EACH H3 (~${wordsPerH3} words)`) : 'Main content'}:
   ${h3s.length > 0 ? (isFAQ ? `• <h3>Question?</h3>
   • <p>Short answer in 1-3 sentences. Max 80 words.</p>
   • NO lists, NO tables, NO multiple paragraphs
   • Direct answer first, then 1-2 supporting details
   • <strong> sparingly for critical terms only
   • Dashes: ONLY hyphen (-) or en dash (–)` : `• <h3>Exact heading from structure</h3>
   • 2-4 paragraphs specifically about this subsection
   • REQUIRED elements:
     - Specific numbers and statistics
     - Comparison tables (<table>)
     - Numbered lists with data (<ol>)
     - Bulleted lists with examples (<ul>)
   • <strong> to highlight key ideas and numbers
   • <blockquote> for important conclusions
   • Dashes: ONLY hyphen (-) or en dash (–), NEVER em dash (—)
   • NO fluff, NO repetition`) : '• Specific, useful content\n   • Lists, examples, tables\n   • Numbers and statistics\n   • <strong> for important ideas\n   • Dashes: ONLY hyphen (-) or en dash (–)'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
HTML OUTPUT FORMAT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Return ONLY HTML${!isIntro ? ` (WITHOUT <h2>, it will be added automatically)` : ''}:

${!isIntro ? `<p>Section intro...</p>` : `<p>Article intro...</p>`}
${h3s.length > 0 ? (isFAQ ? `<h3>First question?</h3>
<p>Brief answer 1-3 sentences.</p>
<h3>Second question?</h3>
<p>Brief answer 1-3 sentences.</p>` : `<h3>First H3 from structure</h3>
<p>Content ~${wordsPerH3} words...</p>
<ul><li>...</li></ul>
<h3>Second H3 from structure</h3>
<p>Content ~${wordsPerH3} words...</p>`) : `<p>Paragraph...</p>
<p>Another paragraph...</p>`}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
}

export function buildAssemblyPrompt(
  outline: { h1: string; metaDescription: string; includeIntroduction?: boolean; includeConclusion?: boolean; includeCta?: boolean; includeFaq?: boolean },
  sections: { h2: string; html: string }[],
  keywords: KeywordData,
  projectContext: ProjectContext,
  language?: string,
  contentType?: string,
  intent?: string,
  extractedData?: any
): string {
  const sectionsContent = sections.map((s, i) => `
=== SECTION ${i + 1}: ${s.h2} ===
${s.html}
`).join('\n');

  const unusedKeywords = keywords.missing.slice(0, 10).join(', ');
  
  // Language instruction НА ТІЙ ЖЕ МОВІ
  const languageInstructions: Record<string, string> = {
    uk: `
═══════════════════════════════════════════════════════════════
🌐 КРИТИЧНА ВИМОГА ДО МОВИ 🌐
═══════════════════════════════════════════════════════════════

Ти МАЄШ писати ВИКЛЮЧНО українською мовою.

ЗАБОРОНЕНО:
❌ Змішувати мови (наприклад, українська + російська)
❌ Використовувати слова з інших мов
❌ Транслітерація або перемикання мов

ОБОВ'ЯЗКОВО:
✅ 100% чиста українська мова
✅ Рідна лексика та граматика
✅ Природні українські вирази

═══════════════════════════════════════════════════════════════
`,
    ru: `
═══════════════════════════════════════════════════════════════
🌐 КРИТИЧЕСКОЕ ТРЕБОВАНИЕ К ЯЗЫКУ 🌐
═══════════════════════════════════════════════════════════════

Ты ДОЛЖЕН писать ИСКЛЮЧИТЕЛЬНО на русском языке.

ЗАПРЕЩЕНО:
❌ Смешивать языки (например, русский + украинский)
❌ Использовать слова из других языков
❌ Транслитерация или переключение языков

ОБЯЗАТЕЛЬНО:
✅ 100% чистый русский язык
✅ Родная лексика и грамматика
✅ Естественные русские выражения

═══════════════════════════════════════════════════════════════
`,
    en: `
═══════════════════════════════════════════════════════════════
🌐 CRITICAL LANGUAGE REQUIREMENT 🌐
═══════════════════════════════════════════════════════════════

You MUST write EXCLUSIVELY in English.

FORBIDDEN:
❌ Mixing languages
❌ Using words from other languages
❌ Transliterations or code-switching

REQUIRED:
✅ 100% pure English
✅ Native vocabulary and grammar
✅ Natural English expressions

═══════════════════════════════════════════════════════════════
`,
    pl: `
═══════════════════════════════════════════════════════════════
🌐 KRYTYCZNY WYMÓG JĘZYKOWY 🌐
═══════════════════════════════════════════════════════════════

MUSISZ pisać WYŁĄCZNIE po polsku.

ZABRONIONE:
❌ Mieszanie języków
❌ Używanie słów z innych języków
❌ Transliteracja lub przełączanie języków

WYMAGANE:
✅ 100% czysty język polski
✅ Rodzima leksyka i gramatyka
✅ Naturalne polskie wyrażenia

═══════════════════════════════════════════════════════════════
`
  };
  
  const languageInstruction = languageInstructions[language || 'uk'] || languageInstructions.en;
  
  // Defaults
  const includeIntro = outline.includeIntroduction !== false;
  const includeConclusion = outline.includeConclusion !== false;
  const includeCta = outline.includeCta !== false;
  const includeFaq = outline.includeFaq === true;

  let taskDescription = 'Assemble all sections into one article';
  const tasks = [];
  let formatExample = '';

  // Introduction
  if (includeIntro) {
    tasks.push(`1. Introduction (200-300 words):
   • Compelling hook in first sentence
   • Why this article matters
   • What reader will learn
   • Integrate 2-3 keywords naturally`);
    formatExample += '<p>Intro...</p>\n';
  }

  // Main sections
  tasks.push(`${includeIntro ? '2' : '1'}. Combine all sections:
   • Keep all <h2> and <h3> headings intact
   • Add smooth transitions between sections
   • Should read as ONE cohesive article`);
  formatExample += '<h2>First section</h2>\n...all section content...\n';

  // Conclusion
  if (includeConclusion) {
    tasks.push(`${tasks.length + 1}. Conclusion (200-300 words):
   • Summarize key points
   • Practical next steps for reader
   • What action reader should take`);
    formatExample += '<h2>Conclusion</h2>\n<p>Summary...</p>\n';
  }

  // CTA
  if (includeCta) {
    tasks.push(`${tasks.length + 1}. CTA block (100-150 words):
   • How ${projectContext.description.split('.')[0]} can help
   • Clear and actionable call-to-action
   • Benefits of using service/product
   • Add button or link`);
    formatExample += '<div class="cta">\n<h3>Ready to try?</h3>\n<p>CTA text...</p>\n</div>\n';
  }

  // FAQ
  if (includeFaq) {
    tasks.push(`${tasks.length + 1}. FAQ section (5-7 questions):
   • Most common questions about the topic
   • Brief comprehensive answer for each
   • Use <h3> for questions
   • Structure as a list`);
    formatExample += '<h2>Frequently Asked Questions (FAQ)</h2>\n<h3>Question 1?</h3>\n<p>Answer...</p>\n';
  }

  return `${languageInstruction}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ARTICLE ASSEMBLY TASK
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Assemble all sections into one cohesive article.

TITLE (H1): ${outline.h1}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PREPARED SECTIONS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${sectionsContent}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
REFERENCE KEYWORDS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Primary: ${keywords.additional.join(', ')}
Additional: ${unusedKeywords}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
COMPANY CONTEXT FOR CTA
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${projectContext.description}

${projectContext.project_prompt ? `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PROJECT-SPECIFIC INSTRUCTIONS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${projectContext.project_prompt}

Apply these instructions throughout assembly.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
` : ''}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ASSEMBLY STEPS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${tasks.join('\n\n')}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
HTML OUTPUT FORMAT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Return ONLY HTML:

${formatExample}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
}

export function buildPolishPrompt(
  articleHTML: string,
  keywords: KeywordData,
  projectContext: ProjectContext,
  language?: string,
  contentType?: string,
  intent?: string,
  extractedData?: any
): string {
  // Language instruction НА ТІЙ ЖЕ МОВІ
  const languageInstructions: Record<string, string> = {
    uk: `
═══════════════════════════════════════════════════════════════
🌐 КРИТИЧНА ВИМОГА ДО МОВИ 🌐
═══════════════════════════════════════════════════════════════

Ти МАЄШ писати ВИКЛЮЧНО українською мовою.

ЗАБОРОНЕНО:
❌ Змішувати мови (наприклад, українська + російська)
❌ Використовувати слова з інших мов
❌ Транслітерація або перемикання мов

ОБОВ'ЯЗКОВО:
✅ 100% чиста українська мова
✅ Рідна лексика та граматика
✅ Природні українські вирази

═══════════════════════════════════════════════════════════════
`,
    ru: `
═══════════════════════════════════════════════════════════════
🌐 КРИТИЧЕСКОЕ ТРЕБОВАНИЕ К ЯЗЫКУ 🌐
═══════════════════════════════════════════════════════════════

Ты ДОЛЖЕН писать ИСКЛЮЧИТЕЛЬНО на русском языке.

ЗАПРЕЩЕНО:
❌ Смешивать языки (например, русский + украинский)
❌ Использовать слова из других языков
❌ Транслитерация или переключение языков

ОБЯЗАТЕЛЬНО:
✅ 100% чистый русский язык
✅ Родная лексика и грамматика
✅ Естественные русские выражения

═══════════════════════════════════════════════════════════════
`,
    en: `
═══════════════════════════════════════════════════════════════
🌐 CRITICAL LANGUAGE REQUIREMENT 🌐
═══════════════════════════════════════════════════════════════

You MUST write EXCLUSIVELY in English.

FORBIDDEN:
❌ Mixing languages
❌ Using words from other languages
❌ Transliterations or code-switching

REQUIRED:
✅ 100% pure English
✅ Native vocabulary and grammar
✅ Natural English expressions

═══════════════════════════════════════════════════════════════
`,
    pl: `
═══════════════════════════════════════════════════════════════
🌐 KRYTYCZNY WYMÓG JĘZYKOWY 🌐
═══════════════════════════════════════════════════════════════

MUSISZ pisać WYŁĄCZNIE po polsku.

ZABRONIONE:
❌ Mieszanie języków
❌ Używanie słów z innych języków
❌ Transliteracja lub przełączanie języków

WYMAGANE:
✅ 100% czysty język polski
✅ Rodzima leksyka i gramatyka
✅ Naturalne polskie wyrażenia

═══════════════════════════════════════════════════════════════
`
  };

  const languageInstruction = languageInstructions[language || 'uk'] || languageInstructions.en;

  return `${languageInstruction}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
CONTENT POLISHING TASK
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are a duplicate detector and spell checker.

ARTICLE TO REVIEW:
${articleHTML}

PRIMARY KEYWORDS: ${keywords.additional.join(', ')}

${projectContext.project_prompt ? `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PROJECT-SPECIFIC INSTRUCTIONS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${projectContext.project_prompt}

Maintain the tone and style defined above.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
` : ''}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PRIORITY TASKS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. FIX SPELLING AND GRAMMAR:
   ✓ Correct spelling mistakes
   ✓ Fix grammatical errors
   ✓ Fix punctuation errors
   ✓ Fix typos
   ✗ Do NOT change terminology or proper names
   ✗ Do NOT change sentence structure unless clearly wrong
   ✗ Do NOT rewrite content - only fix errors

2. REMOVE OBVIOUS DUPLICATES:
   ✓ Keep 95% of article AS IS - minimal changes
   ✓ Look for EXACT or VERY SIMILAR duplicate blocks
   ✓ Same sentence in multiple sections
   ✓ Identical paragraphs with minor word changes
   ✓ Repeated conclusions/summaries
   ✓ When found: DELETE only ONE instance (keep FIRST or BEST)
   ✗ Do NOT merge or rewrite
   ✗ Do NOT remove unique content
   ✗ Do NOT restructure article

3. FAQ SECTION - ABSOLUTELY NO CHANGES:
   ✗ NEVER modify FAQ section
   ✗ FAQ questions can repeat article text - this is NORMAL
   ✗ FAQ answers can duplicate article content - this is REQUIRED
   ✓ Keep ALL FAQ questions and answers EXACTLY as is
   ✓ FAQ section EXCLUDED from deduplication
   ✓ Even 100% repetition in FAQ is acceptable

FAQ IDENTIFICATION:
• H2 containing: "FAQ", "Питання", "Вопрос", "Questions"
• Multiple H3 questions (ending with "?")
• Usually last or near-last section

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
WHAT TO KEEP
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

• Different sections with different information
• Similar topics with different angles
• Keywords used naturally throughout
• Introduction + Conclusion (slight overlap acceptable)
• 100% of FAQ section always
• All unique content and perspectives

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
FORBIDDEN ACTIONS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

✗ Touch FAQ section in any way
✗ Remove FAQ questions even if they repeat article
✗ Remove FAQ answers even if identical to article
✗ Rewrite paragraphs to shorten them
✗ Restructure article
✗ Remove content because it seems "too long"
✗ Merge different sections
✗ Change article flow or order
✗ Remove entire sections
✗ Shorten answers or explanations

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
DUPLICATE EXAMPLES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

REMOVE (obvious duplicates):

Section 2: "Наш сервіс надає якісну підтримку 24/7"
Section 5: "Ми надаємо якісну підтримку 24/7"
→ DELETE one (same information)

Section 3: "Є три типи продуктів: A, B, C"
Section 7: "Пропонуємо три категорії: A, B, C"
→ DELETE one (duplicate list)

KEEP (not duplicates):

Section 2: "Продукт має 5 переваг для бізнесу"
Section 4: "Для особистого використання є 3 переваги"
→ KEEP BOTH (different contexts)

Introduction: "У цій статті розглянемо..."
Conclusion: "Ми розглянули основні аспекти..."
→ KEEP BOTH (normal intro/conclusion structure)

FAQ Section: ANY content
→ KEEP 100% - NEVER TOUCH FAQ

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
OUTPUT FORMAT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Return the COMPLETE article with:
• Only obvious duplicates removed
• Spelling/grammar errors fixed
• Keep 90-95% of original content
• Same structure and sections
• All HTML tags and formatting intact
• NO markdown blocks
• NO explanations
• NO placeholders like "...(content continues)..."
• Start with <h1>, end with last section (FAQ if present)

CRITICAL:
• Return COMPLETE article from start to end
• Do NOT truncate or shorten response
• If unsure if duplicate - KEEP IT
• If in FAQ section - KEEP IT ALWAYS

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
}
